<template>
  <template v-if="isScreenshotUser">
    <div
      v-if="!hidden"
      class="fixed top-0 right-0 z-[99999] m-3 w-80 rounded-2xl bg-black/90 p-4 text-white shadow-2xl backdrop-blur"
      style="padding-top: calc(env(safe-area-inset-top) + 0.75rem)"
    >
      <div class="flex items-center justify-between mb-3">
        <div class="text-base font-bold">Screenshot Dev</div>
        <button
          class="rounded-lg bg-white/15 px-3 py-1.5 text-sm font-semibold hover:bg-white/25"
          @click="hidden = true"
        >
          Hide
        </button>
      </div>

      <div class="grid grid-cols-3 gap-2.5 mb-4">
        <button
          v-for="acc in accounts"
          :key="acc.email"
          :disabled="switching"
          class="rounded-lg! px-3! py-3.5! text-sm font-bold uppercase tracking-wide transition-colors"
          :class="acc.email === currentEmail
            ? 'bg-primary-500 text-ink'
            : 'bg-white/15 text-white hover:bg-white/25 disabled:opacity-50'"
          @click="switchTo(acc)"
        >
          {{ acc.locale.split('-')[0] }}
        </button>
      </div>

      <div class="grid grid-cols-2 gap-2.5 mb-4">
        <button
          v-for="p in pages"
          :key="p.label"
          class="rounded-lg! bg-white/15 px-4! py-3! text-sm font-medium text-left hover:bg-white/25"
          @click="p.route.startsWith('/child-mode') ? goToChildUI(p.route) : goTo(p.route)"
        >
          {{ p.label }}
        </button>
      </div>

      <div class="grid grid-cols-2 gap-2.5">
        <button
          class="rounded-lg! bg-amber-500/80 px-4! py-3! text-sm font-semibold hover:bg-amber-500 disabled:opacity-50"
          :disabled="triggering"
          @click="triggerReward"
        >
          🎁 Reward
        </button>
        <button
          class="rounded-lg! bg-purple-500/80 px-4! py-3! text-sm font-semibold hover:bg-purple-500 disabled:opacity-50"
          :disabled="triggering"
          @click="triggerChest"
        >
          📦 Chest
        </button>
      </div>
    </div>

    <!-- When hidden, the panel is revealed by triple-tapping the Home tab
         button (emits app:screenshot-reveal). No invisible overlay — the page
         content used to steal those taps. -->
  </template>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useAppContextStore } from '@/stores/appContext';
import { supabase } from '@/lib/supabase';
import { eventBus } from '@/utils/eventBus';
import {
  SCREENSHOT_ACCOUNTS,
  SCREENSHOT_PASSWORD,
  isScreenshotEmail,
  type ScreenshotAccount,
} from '@/services/screenshot-accounts.service';
import { supabaseAuthService } from '@/services/supabase/auth.service';
import { SCREENSHOT_SLIDES } from '@/utils/screenshot-slides';

const router = useRouter();
const authStore = useAuthStore();
const appContext = useAppContextStore();

// Hidden by default so it never leaks into screenshots (and the automated
// capture flow stays clean). Reveal it manually via the triple-tap on Home.
const hidden = ref(true);
const switching = ref(false);
const triggering = ref(false);

// Revealed by triple-tapping the Home tab button (see TabsPage).
let offReveal: (() => void) | null = null;
const onHidePanel = () => { hidden.value = true; };
onMounted(() => {
  offReveal = eventBus.on('app:screenshot-reveal', () => { hidden.value = false; });
  window.addEventListener('screenshot:hide-panel', onHidePanel);
});
onUnmounted(() => {
  offReveal?.();
  window.removeEventListener('screenshot:hide-panel', onHidePanel);
});

const accounts = SCREENSHOT_ACCOUNTS;
const currentEmail = computed(() => authStore.user?.email ?? null);
const isScreenshotUser = computed(() => isScreenshotEmail(currentEmail.value));

const pages = SCREENSHOT_SLIDES;

async function switchTo(acc: ScreenshotAccount) {
  if (switching.value || acc.email === currentEmail.value) return;
  switching.value = true;
  try {
    localStorage.setItem('user-locale', acc.locale);
  } catch (_) {
    // no-op: locale persistence is best-effort
  }
  try {
    await supabaseAuthService.logout();
  } catch (_) {
    // no-op: ignore logout failure before re-login
  }
  try {
    await supabaseAuthService.signInWithEmailPassword(acc.email, SCREENSHOT_PASSWORD);
    window.location.href = '/tabs/tasks';
  } catch (e) {
    console.error('[screenshot] switch failed', e);
    switching.value = false;
  }
}

function goTo(route: string) {
  router.push(route);
}

function resolveChildId(): string | null {
  const id = appContext.currentChildId;
  if (id) return id;
  const first = appContext.availableChildren[0];
  if (first) {
    appContext.setCurrentChild(first);
    return first.id;
  }
  return null;
}

async function triggerReward() {
  const childId = resolveChildId();
  if (!childId) {
    console.warn('[screenshot] no child available');
    return;
  }
  triggering.value = true;
  try {
    const { error } = await (supabase as any).rpc('screenshot_trigger_reward_notification', { p_child_id: childId });
    if (error) console.error('[screenshot] trigger reward RPC error', error);
  } finally {
    triggering.value = false;
  }
}

async function triggerChest() {
  const childId = resolveChildId();
  if (!childId) return;
  triggering.value = true;
  try {
    const { error } = await (supabase as any).rpc('screenshot_trigger_chest_notification', { p_child_id: childId });
    if (error) console.error('[screenshot] trigger chest RPC error', error);
  } finally {
    triggering.value = false;
  }
}

async function goToChildUI(route = '/child-mode/tasks') {
  // Ensure a child is selected + child view mode is on so /child-mode/* shows
  // data immediately, whatever the target child-mode screen.
  const child = appContext.currentChild ?? appContext.availableChildren[0];
  if (child) {
    if (!appContext.currentChild) appContext.setCurrentChild(child);
    appContext.enterChildMode(child.id);
  }
  router.push(route);
}
</script>
